import { Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouteConfigLoadEnd, Router } from '@angular/router';
import { takeUntil } from 'rxjs';
import { AuthService } from 'src/app/service/auth.service';
import { StorageService } from 'src/app/service/storage.service';
import { LanguageService } from 'src/app/service/language.service';
import { TranslationService } from 'src/app/service/translation.service';
import { CommonService } from 'src/app/service/common.service';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.css'],
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  isOpen = false;
  isLanguageDropdownOpen = false;
  selectedOption: string = '';
  currentLanguage: string = 'en';
  isMobile: boolean = false;
  sidebarMenuOpen = true;
  languageOptions = [
    { label: 'English', value: 'en', flag: '🇺🇸' },
    { label: 'العربية', value: 'ar', flag: '🇸🇦' }
  ];
  dropdownOptions = [
    {
      'label': 'My Profile',
      'value': 'profile'
    },
    {
      'label': 'Notifications',
      'value': 'notification'
    },
    {
      'label': 'Legal',
      'value': 'legal'
    },
    {
      'label': 'Logout',
      'value': 'logout'
    }
  ];
  userName: string = '';
  isLoggedIn: boolean = false;
  userSubType: string = '';
  private destroy$ = new Subject<void>();
  constructor(
    private authService: AuthService,
    private elementRef: ElementRef,
    private storage: StorageService,
    private languageService: LanguageService,
    public translationService: TranslationService,
    private router: Router,
    private commonService: CommonService
  ){
    this.storage.getItem('local', 'authData').subscribe((userData: any) => {
      if(userData){
        this.userSubType = userData.sub_type;
        this.isLoggedIn = true;
        this.userName = userData.name;
      } else {
        this.isLoggedIn = false;
      }
    });
  }

  ngOnInit(): void {
    // Get current language from storage
    this.currentLanguage = this.languageService.current || 'en';
    this.updateTranslations();
    this.translationService.currentLang$
      .pipe(takeUntil(this.destroy$))
      .subscribe((language) => {
        this.currentLanguage = language || 'en';
        this.updateTranslations();
      });
    
    // Subscribe to mobile state changes
    this.commonService.isMobile$.subscribe(isMobile => {
      this.isMobile = isMobile;
    });
    this.commonService.sidebarOpen$.pipe(takeUntil(this.destroy$)).subscribe((open) => {
      this.sidebarMenuOpen = open;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateTranslations(): void {
    this.languageOptions = [
      { label: this.translationService.instant('header.english', 'English'), value: 'en', flag: '🇺🇸' },
      { label: this.translationService.instant('header.arabic', 'العربية'), value: 'ar', flag: '🇸🇦' }
    ];
    this.dropdownOptions = [
      { 'label': this.translationService.instant('header.myProfile', 'My Profile'), 'value': 'profile' },
      { 'label': this.translationService.instant('header.notification', 'Notifications'), 'value': 'notification' },
      { 'label': this.translationService.instant('header.legal', 'Legal'), 'value': 'legal' },
      { 'label': this.translationService.instant('header.logout', 'Logout'), 'value': 'logout' }
    ];
  }
  
  @HostListener('blur')
  handleBlur() {
    this.isOpen = false;
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.isOpen = false;
      this.isLanguageDropdownOpen = false;
    }
  }

  logout(){
    this.authService.logout().subscribe(resp => {
        this.router.navigate(['/login']);
    });
  }



  toggleDropdown() {
    this.isOpen = !this.isOpen;
  }

  selectOption(value: string) {
    this.selectedOption = value;
    this.isOpen = false;  
    if(value === 'logout'){
      this.logout();
    } else if(value === 'profile'){
      if(this.userSubType === 'ROWNER') {
        this.router.navigate(['/store-profile']);
      } else {
        this.router.navigate(['/profile']);
      }
    } else if (value === 'legal') {
      this.router.navigate(['/legal']);
    }
  }

  navigateFaqs() {
    this.router.navigate(['/faqs']);
  }

  navigateHelp() {
    this.router.navigate(['/help']);
  }

  toggleLanguageDropdown() {
    this.isLanguageDropdownOpen = !this.isLanguageDropdownOpen;
  }

  selectLanguage(language: string) {
    this.isLanguageDropdownOpen = false;
    this.languageService.setLanguage(language);
    this.translationService.setLanguage(language);
    this.currentLanguage = language;
  }

  toggleSidebar(): void {
    const currentState = this.commonService.sidebarOpen$.value;
    this.commonService.sidebarOpen$.next(!currentState);
  }

  private applyLanguage(language: string) {
    this.currentLanguage = language;
    try {
      document.documentElement.lang = language;
      if (language === 'ar') {
        document.documentElement.dir = 'rtl';
        document.body.classList.add('rtl');
      } else {
        document.documentElement.dir = 'ltr';
        document.body.classList.remove('rtl');
      }
    } catch (e) {
      // ignore in non-browser contexts
    }
    // persist selection
    this.storage.setItem('local', 'language', { value: language });
  }

  getCurrentLanguageLabel(): string {
    const lang = this.languageOptions.find(opt => opt.value === this.currentLanguage);
    return lang ? lang.label : 'EN';
  }
}
